import {z} from 'zod';

import {isNil} from 'ramda';
import {isValidDate} from 'ramda-adjunct';

import {DiscountApplicationMethodSchema, DiscountValiditySchema} from '@dms/api/finance';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

const toComparableDate = (validFrom: string) => {
  const date = parseDate(validFrom);

  return isValidDate(date) ? date.toDateString() : null;
};

// Clearing the date picker stores null, so the type errors need our message too, not zod's default.
const discountValiditySchema = DiscountValiditySchema.extend({
  validFrom: z
    .string({
      required_error: i18n.t('general.validations.fieldIsRequired'),
      invalid_type_error: i18n.t('general.validations.fieldIsRequired'),
    })
    .refine((validFrom) => isValidDate(parseDate(validFrom)), {
      message: i18n.t('general.validations.fieldIsRequired'),
    }),
});

const discountValidityListSchema = z
  .array(discountValiditySchema)
  .min(1)
  // Zod runs this refine even when an element above failed, so unparseable dates reach it and are skipped.
  .superRefine((validities, ctx) => {
    const dateStrings = validities.map((validity) => toComparableDate(validity.validFrom));

    dateStrings.forEach((currentDate, index) => {
      if (isNil(currentDate)) {
        return;
      }

      const isDuplicate = dateStrings.indexOf(currentDate) !== dateStrings.lastIndexOf(currentDate);

      if (isDuplicate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.accounting.validations.validFromUnique'),
          path: [index, 'validFrom'],
        });
      }
    });
  });

export const discountApplicationMethodFormSchema = z.object({
  applicationMethod: DiscountApplicationMethodSchema,
  validities: discountValidityListSchema,
});

export type DiscountApplicationMethodFormValues = z.infer<
  typeof discountApplicationMethodFormSchema
>;
